import { Button, CheckboxField, Link } from '@ets-global/b2c-website-ui';
import { fromJS, Map } from 'immutable';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import SummaryBanner from '../../../components/Base/Banner/SummaryBanner';
import ErrorMessage from '../../../components/Base/ErrorMessage/ErrorMessage';
import ConfirmModal from '../../../components/Base/Modal/ConfirmModal';
import { SmallTitle } from '../../../components/Base/Title';
import OrderDetail from '../../../components/Order/Detail';
import OrderDetailSkeleton from '../../../components/Order/Detail/OrderDetailSkeleton';
import { TestTypeProgram } from '../../../constants';
import { PURCHASE_ADDRESS } from '../../../graphql/queries';
import { isSocialSecurityNumberRequired } from '../../../helpers/address.ts';
import { setParam } from '../../../helpers/router';
import { useBasketCookie, useQuery, useRouter } from '../../../hooks';
import useGlobalDocuments from '../../../hooks/useGlobalDocuments';
import { paths } from '../../../paths';
import { AuthContext } from '../../../store/Auth';
import Addresses from './Addresses';
import AddressesListSkeleton from './AddressesListSkeleton';
import Candidates from './Candidates';
import DownloadQuoteLink from './DownloadQuoteLink';

const reducer = (state, action) => {
  switch (action.type) {
    case 'requestCancel':
      return {
        isPurchaseBeingCancelled: false,
        isModalDisplayed: true,
      };
    case 'cancel':
      return {
        isPurchaseBeingCancelled: true,
        isModalDisplayed: false,
      };
    case 'abortCancellation':
      return {
        isPurchaseBeingCancelled: false,
        isModalDisplayed: false,
      };
    default:
      throw new Error(`unable to handle reduce type "${action.type}"`);
  }
};

const SummaryContent = ({ loading, purchase, openSession, productsAndServices, purchaseRefetch, onCancel }) => {
  const history = useHistory();
  const location = useLocation();
  const {
    lang,
    generatePath,
    currentRoute: { params },
  } = useRouter();
  const { t } = useTranslation();
  const { getPromoCodeOrVoucher } = useBasketCookie();
  const promoCodeOrVoucher = getPromoCodeOrVoucher();
  const { isAuthenticated } = useContext(AuthContext);
  const requiresDeliveryAddress = Boolean(purchase.get('requiresDeliveryAddress'));
  const [cookies] = useCookies();

  const [isGTCSChecked, setGTCSChecked] = useState(false);
  const [hasGTCSError, setGTCSError] = useState(false);
  const [areAddressesInvalid, setAddressesInvalid] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSsnRequired, setIsSsnRequired] = useState(false);

  const [cancelPurchaseState, dispatchCancelPurchase] = useReducer(reducer, {
    isModalDisplayed: false,
    isPurchaseBeingCancelled: false,
  });
  const [addressUpdating, setAddressUpdating] = useState(false);
  const {
    globalDocuments: { privacy: privacyPolicyUrl, termsAndConditions: termsAndConditionsUrl },
  } = useGlobalDocuments();
  const {
    data: { purchase: address },
    loading: addressLoading,
    refetch: refetchPurchaseAddress,
  } = useQuery(PURCHASE_ADDRESS, {
    variables: { registrationId: purchase.get('registrationId') },
  });

  useEffect(() => {
    setIsSsnRequired(false);
  }, [address]);

  const canSeePrepTools = (openSession, purchase) => {
    if (purchase && purchase.get('voucher')) {
      return (
        !purchase.get('voucher').get('isCpf') ||
        (purchase.get('voucher').get('prepToolIds') && purchase.get('voucher').get('prepToolIds').length > 0)
      );
    }

    return openSession.get('preparationToolVersions').size;
  };

  const getBackPath = (openSession, purchase) => {
    if (openSession) {
      return generatePath(
        canSeePrepTools(openSession, purchase)
          ? paths.SESSION_CROSS_SELLING
          : paths.SESSION_REGISTER_BY_REGISTRATION_ID,
        {
          sessionId: openSession.get('id').toString(),
          registrationId: params.registrationId,
        }
      );
    }

    if (purchase) {
      return generatePath(paths.PREP_TOOLS_BY_REGISTRATION_ID, {
        registrationId: params.registrationId,
      });
    }

    return paths.SESSION_SEARCH;
  };

  const checkSsnIsRequired = () => {
    const purchaseAddresses = fromJS(address);
    const billingAddress = purchaseAddresses.get('billingAddress');
    if (billingAddress && billingAddress.get('country').get('iso2')) {
      return (
        !billingAddress.get('socialSecurityNumber') &&
        isSocialSecurityNumberRequired(cookies.country, billingAddress.get('country').get('iso2'))
      );
    }

    return false;
  };

  const redirectToPayment = () => {
    if (!isGTCSChecked) {
      setHasSubmitted(true);
      setGTCSError(true);

      return;
    }
    if (checkSsnIsRequired()) {
      setIsSsnRequired(true);

      return;
    }

    if (areAddressesInvalid) {
      setHasSubmitted(true);

      return;
    }

    if (!isAuthenticated) {
      history.push({
        pathname: generatePath(paths.LOGIN),
        search: setParam({
          search: location.search,
          key: 'referrer',
          value: generatePath(paths.PURCHASE_PAYMENT, {
            registrationId: params.registrationId,
          }),
        }),
      });
    } else {
      history.push(
        generatePath(paths.PURCHASE_PAYMENT, {
          registrationId: params.registrationId,
        })
      );
    }
  };

  let bottomContent = <AddressesListSkeleton />;

  if (!addressLoading) {
    const purchaseAddresses = fromJS(address);
    const billingAddressHasEmail =
      purchaseAddresses.get('billingAddress') && purchaseAddresses.get('billingAddress').get('email');

    bottomContent = (
      <>
        <Addresses
          registrationId={purchase.get('registrationId')}
          deliveryAddress={purchaseAddresses.get('deliveryAddress') || new Map()}
          billingAddress={purchaseAddresses.get('billingAddress') || new Map()}
          requiresDeliveryAddress={requiresDeliveryAddress}
          setAddressesInvalid={setAddressesInvalid}
          hasSubmitted={hasSubmitted}
          setAddressesLoading={setAddressUpdating}
          refetchPurchaseAddress={refetchPurchaseAddress}
          purchaseRefetch={purchaseRefetch}
        />
        {isSsnRequired && (
          <div className="basket__ssn_error">
            <ErrorMessage>{t('buying-process.error.ssn_required')}</ErrorMessage>
          </div>
        )}
        <div className="basket__block-actions">
          {purchase.get('totalWithTaxes') > 0 && billingAddressHasEmail && (
            <DownloadQuoteLink registrationId={purchase.get('registrationId')} lang={lang} />
          )}
          <div>
            <CheckboxField
              value={isGTCSChecked}
              id={'accept_conditions'}
              name={'accept_conditions'}
              label={
                openSession === undefined || openSession.get('testingSite') !== null ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('session-summary.purchase.accept_conditions', {
                        termsAndConditionsUrl,
                        privacyPolicyUrl,
                      }),
                    }}
                  />
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('session-summary.purchase.accept_conditions_eproctored', {
                        termsAndConditionsUrl,
                        privacyPolicyUrl,
                      }),
                    }}
                  />
                )
              }
              onChange={() => {
                setGTCSError(isGTCSChecked);
                setGTCSChecked(!isGTCSChecked);
              }}
              required={true}
              hasError={hasGTCSError}
              errorMessage={t('common.form.validation.required')}
              data-cypress="buying-process.GTCS"
            />
            <div className={'basket__block-actions-buttons'}>
              <Button
                onClick={() => dispatchCancelPurchase({ type: 'requestCancel' })}
                className={'button--transparent-light'}
                data-cypress="buying-process.cancel-purchase"
                aria-label={t('session-summary.purchase.cancel_button')}
              >
                <Trans>session-summary.purchase.cancel_button</Trans>
              </Button>
              <Button
                onClick={redirectToPayment}
                loading={addressUpdating}
                disabled={addressUpdating}
                data-cypress="buying-process.payment-link"
                aria-label={t('session-summary.purchase.finalize_button')}
              >
                <Trans>session-summary.purchase.finalize_button</Trans>
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SummaryBanner openSession={!!openSession} />
      <div className="basket container container--full container--blue">
        <div className="container container--padding">
          <>
            {!loading && (
              <Link
                to={getBackPath(openSession, purchase)}
                className="link--reversed page-return"
                icon={'arrow'}
                aria-label={t('session-summary.back')}
              >
                <Trans>session-summary.back</Trans>
              </Link>
            )}
            <SmallTitle className={'basket__title'}>
              <Trans>session-summary.basket.title</Trans>
            </SmallTitle>
            <div className="basket__block basket__block--cart">
              {loading && <OrderDetailSkeleton />}
              {!loading && (
                <>
                  <OrderDetail
                    purchase={purchase}
                    session={openSession}
                    purchaseRefetch={purchaseRefetch}
                    isEditable={true}
                    includedProductsAndServices={
                      productsAndServices ? productsAndServices.includedProductsAndServices : null
                    }
                    isWide={true}
                  />
                  {bottomContent}
                  <ConfirmModal
                    isOpen={cancelPurchaseState.isModalDisplayed}
                    buttons={[
                      {
                        label: t('common.refuse'),
                        className: 'button--transparent-light',
                        onClick: () => dispatchCancelPurchase({ type: 'abortCancellation' }),
                        loading: cancelPurchaseState.isPurchaseBeingCancelled,
                      },
                      {
                        label: t('common.accept'),
                        onClick: () => {
                          dispatchCancelPurchase({ type: 'cancel' });
                          onCancel();
                        },
                        loading: cancelPurchaseState.isPurchaseBeingCancelled,
                        'data-cypress': 'buying-process.confirm-cancel-purchase',
                      },
                    ]}
                    title={t('session-summary.purchase.cancel-modal-title')}
                    onClose={() => dispatchCancelPurchase({ type: 'abortCancellation' })}
                  >
                    <Trans>session-summary.purchase.cancel-modal-text</Trans>
                  </ConfirmModal>
                </>
              )}
            </div>
            {!loading && purchase.get('candidates').size > 0 && openSession && (
              <Candidates
                candidates={purchase.get('candidates').toJS()}
                sessionId={openSession.get('id').toString()}
                registrationId={params.registrationId}
                hasVoucher={promoCodeOrVoucher?.isVoucher}
                notEditable={promoCodeOrVoucher?.isCpf}
                withPicture={
                  openSession.get('testType').get('program') === TestTypeProgram.PP &&
                  openSession.get('testType').get('isPhotoRequired')
                }
                withDateOfBirth={openSession.get('testType').get('isDateOfBirthRequired') || false}
                productsAndServices={productsAndServices}
              />
            )}
          </>
        </div>
      </div>
    </>
  );
};

SummaryContent.propTypes = {
  loading: PropTypes.bool,
  purchase: PropTypes.object,
  openSession: PropTypes.object,
  includedProductsAndServices: PropTypes.array,
  purchaseRefetch: PropTypes.func,
  onCancel: PropTypes.func,
};

export default SummaryContent;
